import React from 'react';
import {
  Alert,
  Card,
  Collapse,
  Descriptions,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import { runtimeQueryKeys } from '../../../services/runtimeQueryKeys';
import { runtimeHistoryService } from '../../../services/runtimeOperationalService';
import useRuntimeQuery from '../../../hooks/useRuntimeQuery';
import {
  RuntimeCorrelationChip,
  RuntimeEmptyState,
  RuntimeErrorState,
  RuntimeLoadingState,
} from '../../common/runtime';
import { buildApiErrorMessage } from '../../../utils/apiError';

const { Panel } = Collapse;
const { Text } = Typography;

const STATUS_COLOR = {
  success: 'green',
  completed: 'green',
  running: 'processing',
  pending: 'gold',
  failed: 'red',
  error: 'red',
  cancelled: 'default',
};

const MAX_JSON_PREVIEW_CHARS = 4000;

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const formatDuration = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return `${numeric} ms`;
};

const statusBadge = (value) => {
  const normalized = String(value || '').toLowerCase();
  const color = STATUS_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(value || '-').toUpperCase()}</Tag>;
};

const safePrettyJson = (value) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch (error) {
    return '{\n  "error": "Nao foi possivel serializar JSON com seguranca."\n}';
  }
};

const asObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value;
};

const toNumberOrZero = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const readCounter = (source, keys) => {
  const safeSource = asObject(source);
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(safeSource, key)) {
      return toNumberOrZero(safeSource[key]);
    }
  }
  return 0;
};

const parseLifecycleCounters = (source) => {
  const safeSource = asObject(source);
  if (Object.keys(safeSource).length === 0) return null;

  return {
    due: readCounter(safeSource, ['due', 'due_count', 'total_due', 'pending_due']),
    processed: readCounter(safeSource, ['processed', 'processed_count', 'applied', 'effectuated']),
    failed: readCounter(safeSource, ['failed', 'failed_count', 'errors', 'error_count']),
    noop: readCounter(safeSource, ['noop', 'noop_count', 'no_op', 'no_op_count', 'skipped']),
    revoked: readCounter(safeSource, ['revoked', 'revoked_count']),
    lock_busy:
      Boolean(safeSource.lock_busy === true) ||
      toNumberOrZero(safeSource.lock_busy) > 0 ||
      toNumberOrZero(safeSource.lock_contention) > 0,
  };
};

const renderLifecycleSummary = (summaryJson) => {
  const safeSummary = asObject(summaryJson);
  const scheduledDowngrade = parseLifecycleCounters(safeSummary.scheduled_downgrade);
  const scheduledCancel = parseLifecycleCounters(safeSummary.scheduled_cancel);

  if (!scheduledDowngrade && !scheduledCancel) {
    return null;
  }

  const entries = [
    { key: 'scheduled_downgrade', label: 'scheduled_downgrade', data: scheduledDowngrade },
    { key: 'scheduled_cancel', label: 'scheduled_cancel', data: scheduledCancel },
  ].filter((entry) => Boolean(entry.data));

  return (
    <Card title="Lifecycle Scheduler Summary">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {entries.map((entry) => (
          <Card key={entry.key} size="small" title={entry.label}>
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="due">{entry.data.due}</Descriptions.Item>
              <Descriptions.Item label="processed">{entry.data.processed}</Descriptions.Item>
              <Descriptions.Item label="failed">{entry.data.failed}</Descriptions.Item>
              <Descriptions.Item label="noop">{entry.data.noop}</Descriptions.Item>
              <Descriptions.Item label="revoked">{entry.data.revoked}</Descriptions.Item>
              <Descriptions.Item label="lock_busy">
                {entry.data.lock_busy ? <Tag color="orange">SIM</Tag> : <Tag>NAO</Tag>}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ))}
      </Space>
    </Card>
  );
};

const summarizeEvent = (event) => {
  const bits = [];
  if (event?.tenant_id) bits.push(`tenant: ${event.tenant_id}`);
  if (event?.invoice_id) bits.push(`invoice: ${event.invoice_id}`);
  if (event?.charge_id) bits.push(`charge: ${event.charge_id}`);
  if (event?.issue_id) bits.push(`issue: ${event.issue_id}`);

  const payload = event?.payload_json && typeof event.payload_json === 'object' ? event.payload_json : null;
  if (payload?.stage) bits.push(`stage: ${payload.stage}`);
  if (payload?.operation) bits.push(`operation: ${payload.operation}`);
  if (payload?.error_type) bits.push(`error_type: ${payload.error_type}`);
  if (payload?.provider_status) bits.push(`provider_status: ${payload.provider_status}`);
  if (payload?.provider_charge_ref) bits.push(`provider_charge_ref: ${payload.provider_charge_ref}`);
  if (payload?.provider_invoice_ref) bits.push(`provider_invoice_ref: ${payload.provider_invoice_ref}`);
  if (payload?.provider_customer_ref) bits.push(`provider_customer_ref: ${payload.provider_customer_ref}`);

  return bits.length ? bits.join(' | ') : 'Sem metadata adicional para este evento.';
};

const JsonPreview = ({ title, data, defaultOpen = false }) => {
  const pretty = React.useMemo(() => safePrettyJson(data), [data]);
  const truncated = pretty.length > MAX_JSON_PREVIEW_CHARS;
  const preview = truncated ? `${pretty.slice(0, MAX_JSON_PREVIEW_CHARS)}\n... [truncado]` : pretty;

  return (
    <Collapse ghost defaultActiveKey={defaultOpen ? ['json'] : []}>
      <Panel header={title} key="json">
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 380,
            overflow: 'auto',
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: 12,
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          {preview}
        </pre>
        {truncated ? (
          <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
            JSON truncado para proteger renderizacao.
          </Text>
        ) : null}
      </Panel>
    </Collapse>
  );
};

const RuntimeExecutionDetailPanel = ({ executionId }) => {
  const normalizedExecutionId = String(executionId || '').trim();

  const {
    data: detailResponse,
    isLoading: isLoadingDetail,
    isError: isDetailError,
    error: detailError,
    refetch: refetchDetail,
  } = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionDetail(normalizedExecutionId),
    enabled: Boolean(normalizedExecutionId),
    queryFn: async () => {
      const response = await runtimeHistoryService.getExecutionDetail(normalizedExecutionId);
      return response.data;
    },
  });

  const {
    data: timelineResponse,
    isLoading: isLoadingTimeline,
    isError: isTimelineError,
    error: timelineError,
    refetch: refetchTimeline,
  } = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionTimeline(normalizedExecutionId),
    enabled: Boolean(normalizedExecutionId),
    queryFn: async () => {
      const response = await runtimeHistoryService.getExecutionTimeline(normalizedExecutionId);
      return response.data;
    },
  });

  const execution = detailResponse || null;
  const items = Array.isArray(execution?.items) ? execution.items : [];
  const timelineEvents = Array.isArray(timelineResponse?.events) ? timelineResponse.events : [];

  const detailApiError = isDetailError
    ? buildApiErrorMessage(detailError, 'Nao foi possivel carregar o detalhe da execucao.')
    : null;
  const timelineApiError = isTimelineError
    ? buildApiErrorMessage(timelineError, 'Nao foi possivel carregar a timeline da execucao.')
    : null;

  const itemColumns = React.useMemo(
    () => [
      {
        title: 'Created At',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (value) => formatDateTime(value),
      },
      {
        title: 'Tenant',
        dataIndex: 'tenant_id',
        key: 'tenant_id',
        render: (value) => value || '-',
      },
      {
        title: 'Invoice',
        dataIndex: 'invoice_id',
        key: 'invoice_id',
        render: (value) => value || '-',
      },
      {
        title: 'Charge',
        dataIndex: 'charge_id',
        key: 'charge_id',
        render: (value) => value || '-',
      },
      {
        title: 'Issue',
        dataIndex: 'issue_id',
        key: 'issue_id',
        render: (value) => value || '-',
      },
      {
        title: 'Resultado',
        key: 'result',
        render: (_, row) => row.execution_result || row.simulated_result || row.mutation_summary || '-',
      },
    ],
    []
  );

  if (!normalizedExecutionId) {
    return (
      <RuntimeErrorState
        title="Execution ID invalido"
        description="Nao foi possivel identificar a execucao solicitada."
      />
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {isLoadingDetail ? <RuntimeLoadingState tip="Carregando detalhe da execucao..." /> : null}

      {!isLoadingDetail && isDetailError ? (
        <RuntimeErrorState
          title="Erro ao carregar detalhe da execucao"
          description={detailApiError?.message || 'Falha inesperada ao consultar detalhe.'}
          onRetry={() => refetchDetail()}
        />
      ) : null}

      {!isLoadingDetail && !isDetailError && !execution ? (
        <RuntimeEmptyState
          title="Execucao nao encontrada"
          description="Nao ha dados para o execution_id informado."
        />
      ) : null}

      {!isLoadingDetail && !isDetailError && execution ? (
        <>
          <Card>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Execution ID">{execution.execution_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="Correlation ID">
                <RuntimeCorrelationChip correlationId={execution.correlation_id} />
              </Descriptions.Item>
              <Descriptions.Item label="Tenant ID">{execution.tenant_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="Operation Type">{execution.operation_type || '-'}</Descriptions.Item>
              <Descriptions.Item label="Provider">{execution.provider_key || '-'}</Descriptions.Item>
              <Descriptions.Item label="Source">{execution.source || '-'}</Descriptions.Item>
              <Descriptions.Item label="Status">{statusBadge(execution.status)}</Descriptions.Item>
              <Descriptions.Item label="Dry Run">
                {execution.dry_run ? <Tag color="blue">SIM</Tag> : <Tag>NAO</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Started At">{formatDateTime(execution.started_at)}</Descriptions.Item>
              <Descriptions.Item label="Finished At">{formatDateTime(execution.finished_at)}</Descriptions.Item>
              <Descriptions.Item label="Duration">{formatDuration(execution.duration_ms)}</Descriptions.Item>
              <Descriptions.Item label="Actor User ID">{execution.actor_user_id || '-'}</Descriptions.Item>
            </Descriptions>
          </Card>

          {execution.error_message ? (
            <Alert
              type="error"
              showIcon
              message={execution.error_type || 'Erro operacional na execucao'}
              description={execution.error_message}
            />
          ) : null}

          {renderLifecycleSummary(execution.summary_json)}

          <Card title="Runtime Metadata">
            <JsonPreview title="Visualizar summary_json" data={execution.summary_json} defaultOpen />
          </Card>

          <Card title="Payload Sanitizado">
            <JsonPreview
              title="Visualizar payload sanitizado"
              data={execution.payload_sanitized || execution.summary_json || {}}
              defaultOpen
            />
          </Card>

          <Card title="Timeline / Event Stream">
            {isLoadingTimeline ? <RuntimeLoadingState tip="Carregando timeline da execucao..." /> : null}

            {!isLoadingTimeline && isTimelineError ? (
              <RuntimeErrorState
                title="Erro ao carregar timeline"
                description={timelineApiError?.message || 'Falha inesperada ao consultar timeline.'}
                onRetry={() => refetchTimeline()}
              />
            ) : null}

            {!isLoadingTimeline && !isTimelineError && timelineEvents.length === 0 ? (
              <RuntimeEmptyState
                title="Timeline vazia"
                description="Nao existem eventos para esta execucao."
              />
            ) : null}

            {!isLoadingTimeline && !isTimelineError && timelineEvents.length > 0 ? (
              <Timeline
                items={timelineEvents.map((event, index) => ({
                  color: String(event?.event_type || '').toLowerCase().includes('fail') ? 'red' : 'blue',
                  children: (
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space wrap size={8}>
                        <Tag color="default">{String(event?.event_type || '-').toUpperCase()}</Tag>
                        <Text type="secondary">{formatDateTime(event?.event_at)}</Text>
                        <RuntimeCorrelationChip correlationId={event?.correlation_id} tooltip="Buscar correlation_id" />
                      </Space>
                      <Text>{summarizeEvent(event)}</Text>
                      <JsonPreview
                        title={`Payload do evento #${index + 1}`}
                        data={event?.payload_json || {}}
                      />
                    </Space>
                  ),
                }))}
              />
            ) : null}
          </Card>

          <Card title="Execution Items">
            {items.length === 0 ? (
              <RuntimeEmptyState
                title="Sem itens na execucao"
                description="A execucao nao possui itens associados."
              />
            ) : (
              <Table
                rowKey={(record, idx) => `${record?.invoice_id || 'no-invoice'}-${record?.charge_id || 'no-charge'}-${idx}`}
                columns={itemColumns}
                dataSource={items}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
              />
            )}
          </Card>
        </>
      ) : null}
    </Space>
  );
};

export default RuntimeExecutionDetailPanel;